import { Button, ButtonVariants } from '@components/button/Button';
import { Icon } from '@components/icon/Icon';
import { getCurrentTimeOfTheRestaurant } from '@shared/utils/dateFormatters';
import cx from 'classnames';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import typography from '~styles/typography.scss';
import { formatLocationAddress } from './formatLocationAddress';
import { useLightspeedBusinessLocations } from './hooks';
import { LightspeedLocationPicker } from './LightspeedLocationPicker';
import styles from './LightspeedSettingsPage.scss';

export interface LightspeedConnectionCardProps {
  restaurantId: string;
  businessLocationId: string | null;
  needsReauthorization: boolean;
  recentPushFailedAt: string | null;
  isReconnecting: boolean;
  onReconnect: () => void;
  onDisconnect: () => void;
  onLocationLinked: () => void;
}

export const LightspeedConnectionCard = ({
  restaurantId,
  businessLocationId,
  needsReauthorization,
  recentPushFailedAt,
  isReconnecting,
  onReconnect,
  onDisconnect,
  onLocationLinked,
}: LightspeedConnectionCardProps) => {
  const { timezone } = useRestaurant();
  const hasPushFailureNotice =
    recentPushFailedAt !== null && !needsReauthorization;

  // The name and address only make the card easier to read, so a failed lookup leaves the
  // card showing the connection without them rather than an error. The lookup needs a
  // working token, so it is skipped while the restaurant has to reconnect.
  const { data: locations } = useLightspeedBusinessLocations(restaurantId, {
    enabled: Boolean(businessLocationId) && !needsReauthorization,
  });
  const location = locations?.find(
    (candidate) => candidate.businessLocationId === businessLocationId,
  );

  const address = location ? formatLocationAddress(location) : '';

  return (
    <section className={styles.card} aria-label="Lightspeed connection">
      <div className={styles.cardHeader}>
        <span
          className={cx(typography.t2, styles.statusPill, {
            [styles.statusPillWarning]: needsReauthorization,
          })}
          role="status"
        >
          <span
            className={cx(styles.statusDot, {
              [styles.statusDotWarning]: needsReauthorization,
            })}
            aria-hidden
          />
          {needsReauthorization ? 'Reconnection required' : 'Connected'}
        </span>
        <div className={styles.cardActions}>
          {needsReauthorization && (
            <Button
              type="button"
              variant={ButtonVariants.Primary}
              onClick={onReconnect}
              isDisabled={isReconnecting}
            >
              Reconnect Lightspeed
            </Button>
          )}
          <Button
            type="button"
            variant={ButtonVariants.Secondary}
            onClick={onDisconnect}
          >
            Disconnect Lightspeed
          </Button>
        </div>
      </div>

      {needsReauthorization && (
        <div className={styles.warningBox} role="alert">
          <Icon name="alertTriangle" className={styles.warningIcon} />
          <div className={styles.warningText}>
            <p className={cx(typography.t2, styles.warningTitle)}>
              Peak can no longer reach Lightspeed
            </p>
            <p className={cx(typography.t2, styles.muted)}>
              Reservations and walk-ins are not being sent to Lightspeed. Sign
              in to Lightspeed again to resume. Orders for reservations already
              sent to Lightspeed keep arriving.
            </p>
          </div>
        </div>
      )}

      {hasPushFailureNotice && (
        <div className={styles.warningBox} role="alert">
          <Icon name="alertTriangle" className={styles.warningIcon} />
          <div className={styles.warningText}>
            <p className={cx(typography.t2, styles.warningTitle)}>
              A recent update could not be sent to Lightspeed
            </p>
            <p className={cx(typography.t2, styles.muted)}>
              The last problem was on{' '}
              {getCurrentTimeOfTheRestaurant(
                recentPushFailedAt ?? '',
                timezone,
              )}
              . The affected reservation will sync the next time it changes. If
              this keeps happening, contact Peak support.
            </p>
          </div>
        </div>
      )}

      {!needsReauthorization &&
        (businessLocationId ? (
          <>
            {location && (
              <div className={styles.locationDetails}>
                <p className={cx(typography.h5, styles.locationTitle)}>
                  {location.name ?? 'Lightspeed location'}
                </p>
                {address && (
                  <p className={cx(typography.t2, styles.muted)}>{address}</p>
                )}
              </div>
            )}
            <div className={styles.syncSummary}>
              <p className={cx(typography.t2, styles.syncHeading)}>
                What this connection does
              </p>
              <ul className={cx(typography.t2, styles.syncList)}>
                <li>Reservations sync to Lightspeed</li>
                <li>Orders from Lightspeed show in Peak</li>
              </ul>
            </div>
          </>
        ) : (
          <LightspeedLocationPicker
            restaurantId={restaurantId}
            onLinked={onLocationLinked}
          />
        ))}
    </section>
  );
};
